import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { encryptText, decryptText, tryUnlock } from "@/lib/crypto";
import { toast } from "sonner";
import {
    LockKey, LockKeyOpen, Trash, Plus, ChatText, Key, LinkSimple, FloppyDisk, Eye, EyeSlash,
} from "@phosphor-icons/react";

const KINDS = ["message", "password", "document_link"];

export default function Safe() {
    const { t, masterPassword, setMasterPassword } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [unlockPwd, setUnlockPwd] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [decrypted, setDecrypted] = useState({}); // item_id -> plaintext

    const [newKind, setNewKind] = useState("message");
    const [newTitle, setNewTitle] = useState("");
    const [newContent, setNewContent] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/vault/items");
            setItems(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const handleUnlock = async (e) => {
        e.preventDefault();
        const ok = await tryUnlock(items, unlockPwd);
        if (!ok) {
            toast.error("Falsches Master-Passwort");
            return;
        }
        setMasterPassword(unlockPwd);
        setUnlockPwd("");
        toast.success("Safe entsperrt");
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!newTitle.trim() || !newContent.trim()) return;
        try {
            const enc = await encryptText(newContent, masterPassword);
            const r = await api.post("/vault/items", {
                kind: newKind,
                title: newTitle,
                ciphertext: enc.ciphertext,
                iv: enc.iv,
                salt: enc.salt,
            });
            setItems((prev) => [...prev, r.data]);
            setNewTitle(""); setNewContent(""); setShowAdd(false);
            toast.success(t("safe.savedEnc"));
        } catch (err) {
            toast.error("Verschlüsselung fehlgeschlagen");
        }
    };

    const handleReveal = async (item) => {
        if (decrypted[item.item_id]) {
            setDecrypted((d) => { const n = { ...d }; delete n[item.item_id]; return n; });
            return;
        }
        try {
            const pt = await decryptText(item, masterPassword);
            setDecrypted((d) => ({ ...d, [item.item_id]: pt }));
        } catch {
            toast.error("Entschlüsselung fehlgeschlagen");
        }
    };

    const handleDelete = async (item_id) => {
        try {
            await api.delete(`/vault/items/${item_id}`);
            setItems((prev) => prev.filter((i) => i.item_id !== item_id));
            toast.success(t("safe.deleted"));
        } catch {
            toast.error("Fehler");
        }
    };

    // Unlock screen
    if (!masterPassword) {
        return (
            <AppShell>
                <div className="mx-auto max-w-xl px-6 lg:px-10 py-16 lg:py-24">
                    <div className="card-soft p-8 lg:p-10" data-testid="unlock-card">
                        <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-shield-forest text-white">
                            <LockKey size={26} weight="duotone" aria-hidden="true" />
                        </div>
                        <h1 className="mt-6 font-heading font-bold text-shield-ink text-2xl sm:text-3xl">
                            {t("safe.unlockTitle")}
                        </h1>
                        <p className="mt-3 text-shield-muted leading-relaxed">{t("safe.unlockSub")}</p>

                        <form onSubmit={handleUnlock} className="mt-6 space-y-4">
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={unlockPwd}
                                onChange={(e) => setUnlockPwd(e.target.value)}
                                className="w-full h-14 rounded-xl border-2 border-shield-line px-4 text-lg focus:border-shield-forest focus:outline-none focus:ring-4 focus:ring-shield-forest/20 bg-white"
                                placeholder="Master-Passwort"
                                data-testid="master-password-input"
                                autoFocus
                            />
                            <button
                                type="submit"
                                className="w-full h-14 pill bg-shield-forest text-white text-lg font-semibold hover:bg-shield-forestDeep transition-colors"
                                data-testid="unlock-btn"
                            >
                                {t("safe.unlockBtn")}
                            </button>
                        </form>
                        <p className="mt-4 text-xs text-shield-muted">
                            Tipp: Beim ersten Mal wird dein Master-Passwort automatisch mit deinem ersten Eintrag verknüpft.
                        </p>
                    </div>
                </div>
            </AppShell>
        );
    }

    return (
        <AppShell>
            <div className="mx-auto max-w-4xl px-6 lg:px-10 py-10 lg:py-14">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                            <LockKeyOpen size={14} weight="fill" aria-hidden="true" /> Safe
                        </div>
                        <h1 className="mt-2 font-heading font-bold text-shield-ink text-3xl sm:text-4xl" data-testid="safe-title">
                            {t("safe.title")}
                        </h1>
                        <p className="mt-2 text-shield-muted">{t("safe.sub")}</p>
                    </div>
                    <button
                        onClick={() => setShowAdd((s) => !s)}
                        className="inline-flex items-center gap-2 h-12 px-5 pill bg-shield-forest text-white font-semibold hover:bg-shield-forestDeep transition-colors"
                        data-testid="safe-add-btn"
                    >
                        <Plus size={16} weight="bold" aria-hidden="true" />
                        {t("safe.addNew")}
                    </button>
                </div>

                {showAdd && (
                    <form onSubmit={handleAdd} className="mt-8 card-soft p-6 lg:p-8 space-y-4" data-testid="safe-add-form">
                        <div className="grid sm:grid-cols-3 gap-2">
                            {KINDS.map((k) => (
                                <label key={k} className={`cursor-pointer border-2 rounded-xl p-4 flex items-center gap-2 font-medium transition-colors ${newKind === k ? "border-shield-forest bg-shield-forest/5" : "border-shield-line hover:border-shield-forest/40"}`}>
                                    <input
                                        type="radio" name="kind" value={k}
                                        checked={newKind === k}
                                        onChange={() => setNewKind(k)}
                                        className="sr-only"
                                        data-testid={`kind-radio-${k}`}
                                    />
                                    {k === "message" && <ChatText size={20} weight="duotone" aria-hidden="true" />}
                                    {k === "password" && <Key size={20} weight="duotone" aria-hidden="true" />}
                                    {k === "document_link" && <LinkSimple size={20} weight="duotone" aria-hidden="true" />}
                                    {k === "message" ? t("safe.kindMessage") : k === "password" ? t("safe.kindPassword") : t("safe.kindDocument")}
                                </label>
                            ))}
                        </div>
                        <input
                            type="text" required value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            className="w-full h-14 rounded-xl border-2 border-shield-line px-4 text-lg focus:border-shield-forest focus:outline-none bg-white"
                            placeholder={t("safe.titleField")}
                            data-testid="new-title-input"
                        />
                        <textarea
                            required value={newContent}
                            onChange={(e) => setNewContent(e.target.value)}
                            className="w-full rounded-xl border-2 border-shield-line p-4 text-base focus:border-shield-forest focus:outline-none min-h-[140px] bg-white"
                            placeholder={t("safe.contentField")}
                            data-testid="new-content-input"
                        />
                        <button
                            type="submit"
                            className="inline-flex items-center gap-2 h-12 px-6 pill bg-shield-terracotta text-white font-semibold hover:bg-shield-terracottaDeep transition-colors"
                            data-testid="safe-save-btn"
                        >
                            <FloppyDisk size={16} weight="bold" aria-hidden="true" />
                            {t("safe.saveEncrypt")}
                        </button>
                    </form>
                )}

                <div className="mt-10 space-y-4">
                    {loading ? (
                        <p className="text-shield-muted">{t("common.loading")}</p>
                    ) : items.length === 0 ? (
                        <div className="card-soft p-8 text-center text-shield-muted">{t("safe.empty")}</div>
                    ) : items.map((it) => (
                        <div key={it.item_id} className="card-soft p-6 flex items-start justify-between gap-4" data-testid={`safe-item-${it.item_id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted">
                                    {it.kind === "message" && <ChatText size={12} weight="fill" aria-hidden="true" />}
                                    {it.kind === "password" && <Key size={12} weight="fill" aria-hidden="true" />}
                                    {it.kind === "document_link" && <LinkSimple size={12} weight="fill" aria-hidden="true" />}
                                    {it.kind}
                                </div>
                                <div className="mt-1 font-heading font-semibold text-shield-ink text-lg break-words">{it.title}</div>
                                {decrypted[it.item_id] ? (
                                    <pre className="mt-3 whitespace-pre-wrap break-words text-shield-ink bg-shield-sage/40 p-3 rounded-lg text-sm">{decrypted[it.item_id]}</pre>
                                ) : (
                                    <div className="mt-3 text-shield-muted text-sm font-mono truncate" title="verschlüsselt">
                                        {"█".repeat(24)}
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={() => handleReveal(it)}
                                    className="inline-flex items-center gap-1.5 h-10 px-3 pill text-shield-forest bg-shield-sage/60 hover:bg-shield-sage font-semibold transition-colors"
                                    data-testid={`safe-reveal-${it.item_id}`}
                                >
                                    {decrypted[it.item_id] ? <EyeSlash size={14} weight="bold" aria-hidden="true" /> : <Eye size={14} weight="bold" aria-hidden="true" />}
                                </button>
                                <button
                                    onClick={() => handleDelete(it.item_id)}
                                    className="inline-flex items-center gap-1.5 h-10 px-3 pill text-shield-error hover:bg-shield-error/10 font-semibold transition-colors"
                                    data-testid={`safe-delete-${it.item_id}`}
                                >
                                    <Trash size={14} weight="bold" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </AppShell>
    );
}
